import { logger } from './logger';
import { ChatMessage } from '../hooks/useChatMessages';

/**
 * Creates a notification when a new chat message is received
 * NOTE: Chat notifications are NOT created in the database anymore.
 * They are only shown in the floating chat button using useUnreadMessageCount.
 * This prevents chat notifications from appearing in notification bells.
 * @param _message The chat message that was received
 * @param _currentUserId The ID of the current user (to avoid notifying themselves)
 */
export async function createChatNotification(
  _message: ChatMessage,
  _currentUserId: string | null
): Promise<void> {
  // Chat notifications are no longer created in the database
  // They are handled by the floating chat button using useUnreadMessageCount
  // This prevents them from appearing in notification bells (header, carrier portal, my quotes)
  logger.log('💬 Chat notification skipped (handled by floating chat button):', {
    messageId: _message.id,
    conversationId: _message.conversation_id,
    sender: _message.sender_name,
  });
  return;
  
  /* DISABLED - Chat notifications no longer go to notification bells
  try {
    // Don't create notification if user sent the message themselves
    if (currentUserId && message.sender_id === currentUserId) {
      return;
    }

    const conversationId = message.conversation_id;
    let recipientUserId: string | null = null;
    let recipientRole: string | null = null;
    let notificationTitle = '';
    let notificationMessage = '';
    let notificationLink = '';
    let notificationMetadata: any = {
      conversation_id: conversationId,
      message_id: message.id,
      sender_id: message.sender_id,
      sender_name: message.sender_name,
      sender_role: message.sender_role,
    };

    // Check if conversation_id is a user_id (support conversation)
    const { data: conversationUser } = await supabase
      .from('profiles')
      .select('id, role, email')
      .eq('id', conversationId)
      .single();

    if (conversationUser) {
      // This is a support conversation (conversation_id = user_id)
      // If sender is user/carrier, notify agent first, but also admin
      // If sender is admin/agent, recipient is the user
      if (message.sender_role === 'user' || message.sender_role === 'carrier') {
        // Notify all agents first (primary), then all admins (secondary)
        // This allows agent to respond first, but admin can intervene if needed
        recipientUserId = null; // Will notify all admins/agents
        recipientRole = 'admin'; // Will be used to filter (includes both admin and agent)
        notificationTitle = 'New chat message';
        notificationLink = `/admin/chat`;
        notificationMetadata.user_id = conversationId;
        notificationMetadata.priority = 'agent'; // Mark for agent priority
      } else {
        // Admin/agent sent message, notify the user
        recipientUserId = conversationId;
        recipientRole = conversationUser.role;
        notificationTitle = 'New support message';
        notificationLink = '/';
        notificationMetadata.user_id = conversationId;
      }
    } else if (conversationId === 'admin-agent') {
      // Special case: admin-agent communication
      // Notify the other admin/agent
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Find the other admin/agent (not the sender)
        const { data: otherAdminAgents } = await supabase
          .from('profiles')
          .select('id, email, role')
          .in('role', ['admin', 'agent'])
          .neq('id', message.sender_id);
        
        if (otherAdminAgents && otherAdminAgents.length > 0) {
          recipientUserId = null; // Will notify all other admins/agents
          recipientRole = 'admin'; // Includes both admin and agent
          notificationTitle = 'New team message';
          notificationLink = `/admin/chat`;
          notificationMetadata.conversation_type = 'admin-agent';
        }
      }
    } else {
      // This might be a quote-based conversation
      const { data: quote } = await supabase
        .from('quotes')
        .select('id, email, assigned_carrier_id')
        .eq('id', conversationId)
        .single();

      if (quote) {
        // Determine recipient based on sender role
        if (message.sender_role === 'admin' || message.sender_role === 'agent') {
          // Admin/agent sent, notify user or carrier
          if (quote.assigned_carrier_id) {
            // Notify carrier
            const { data: carrier } = await supabase
              .from('carrier_registrations')
              .select('email')
              .eq('id', quote.assigned_carrier_id)
              .single();
            if (carrier) {
              const { data: carrierProfile } = await supabase
                .from('profiles')
                .select('id, role')
                .eq('email', carrier.email)
                .single();
              if (carrierProfile) {
                recipientUserId = carrierProfile.id;
                recipientRole = 'carrier';
                notificationTitle = 'New message about assigned quote';
                notificationLink = '/carrier/quotes';
                notificationMetadata.quote_id = conversationId;
                notificationMetadata.carrier_id = carrierProfile.id;
              }
            }
          } else {
            // Notify user
            const { data: userProfile } = await supabase
              .from('profiles')
              .select('id, role')
              .eq('email', quote.email)
              .single();
            if (userProfile) {
              recipientUserId = userProfile.id;
              recipientRole = userProfile.role;
              notificationTitle = 'New message about your quote';
              notificationLink = '/my-quotes';
              notificationMetadata.quote_id = conversationId;
            }
          }
        } else if (message.sender_role === 'user' || message.sender_role === 'carrier') {
          // User/carrier sent, notify admin/agent
          recipientUserId = null; // Will notify all admins/agents
          recipientRole = 'admin';
          notificationTitle = 'New message in quote';
          notificationLink = `/admin/quotes?quote=${conversationId}`;
          notificationMetadata.quote_id = conversationId;
        }
      }
    }

    if (!recipientRole) {
      logger.log('⚠️ Could not determine notification recipient, skipping');
      return;
    }

    notificationMessage = `${message.sender_name}: ${message.message.substring(0, 50)}${message.message.length > 50 ? '...' : ''}`;

    // Create notification(s) in database
    // If recipientUserId is null, we need to notify all admins/agents
    if (recipientUserId === null && (recipientRole === 'admin' || recipientRole === 'agent')) {
      // Get all admin and agent profiles with emails (optimized query)
      // If priority is 'agent', notify agents first, then admins
      let adminAgentsQuery = supabase
        .from('profiles')
        .select('id, email, role')
        .in('role', ['admin', 'agent'])
        .limit(100);

      // If priority is agent, get agents first
      if (notificationMetadata.priority === 'agent') {
        adminAgentsQuery = adminAgentsQuery.order('role', { ascending: true }); // 'agent' comes before 'admin'
      }

      const { data: adminAgents, error: fetchError } = await adminAgentsQuery;

      if (fetchError) {
        logger.error('❌ Error fetching admin/agent profiles:', fetchError);
        return;
      }

      // Create notification for each admin/agent (batch insert for better performance)
      if (adminAgents && adminAgents.length > 0) {
        const notifications = adminAgents.map((profile) => ({
          type: 'chat',
          user_email: profile.email, // Required field
          title: notificationTitle,
          message: notificationMessage,
          link: notificationLink,
          metadata: {
            ...notificationMetadata,
            recipient_id: profile.id,
            recipient_role: profile.role,
          },
        }));

        // Use batch insert for better performance
        const { error: notificationError } = await supabase
          .from('notifications')
          .insert(notifications);

        if (notificationError) {
          logger.error('❌ Error creating chat notifications for admins/agents:', notificationError);
        } else {
          logger.log(`✅ Chat notifications created successfully for ${adminAgents.length} admin(s)/agent(s)`);
        }
      }
    } else if (recipientUserId) {
      // Get recipient email
      const { data: recipientProfile, error: recipientError } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', recipientUserId)
        .single();

      if (recipientError || !recipientProfile) {
        logger.error('❌ Error fetching recipient profile:', recipientError);
        return;
      }

      // Create notification for specific user
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          type: 'chat',
          user_email: recipientProfile.email, // Required field
          title: notificationTitle,
          message: notificationMessage,
          link: notificationLink,
          metadata: {
            ...notificationMetadata,
            recipient_id: recipientUserId,
          },
        });

      if (notificationError) {
        logger.error('❌ Error creating chat notification:', notificationError);
        logger.error('Notification data:', {
          type: 'chat',
          user_email: recipientProfile.email,
          title: notificationTitle,
          message: notificationMessage,
          link: notificationLink,
          metadata: {
            ...notificationMetadata,
            recipient_id: recipientUserId,
          },
        });
      } else {
        logger.log('✅ Chat notification created successfully');
      }
    }
  } catch (error) {
    logger.error('❌ Error in createChatNotification:', error);
  }
  */
}
